import React from "react";
import { animated, Spring } from "react-spring";
import type { GroupedVirtuosoHandle } from "react-virtuoso";
import { GroupedVirtuoso } from "react-virtuoso";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";

import { ExpandLess as ExpandLessIcon } from "@eroad/icons/material";

import type { CoretexTheme } from "../theme";
import CardLoading from "./Card/CardLoading";

const AnimatedExpandLessIcon = animated(ExpandLessIcon);

const animationPaperConfig = {
  mass: 2,
  friction: 20,
  tension: 120,
  clamp: true,
};

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "4px",
      backgroundColor: theme.palette.mono.maxLightGray,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.mono.mediumGray,
      borderRadius: "4px",
    },
  },
  groupHeaderContainer: {
    display: "flex",
    flexDirection: "column",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    height: "32px",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.mono.lightGray,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold as number,
    color: theme.palette.mono.secondaryDark,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  groupTitle: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  groupEndTitle: {
    textTransform: "none",
  },
  icon: {
    color: theme.palette.mono.secondaryDark,
    marginRight: theme.spacing(2),
  },
}));

export interface Group {
  id: string | number;
  title: string;
  endTitle?: string;
  records: number;
  loadItems: () => Promise<unknown[]>;
  headerOnToggle?: (event?: React.MouseEvent) => void;
  headerOnMouseEnter?: (event: React.MouseEvent) => void;
  headerOnMouseLeave?: (event: React.MouseEvent) => void;
  data?: unknown[];
  className?: string;
  expandOnLoad?: boolean;
}

export interface GroupedListProps {
  groups: Group[];

  renderItem: (index: number, data: any[], groupIndex: number) => React.ReactNode;
  className?: string;
  hideRecords?: boolean;
  resetItems?: boolean;
  scrollToIndex?: number;
  toggleGroupIndex?: number;
  initialItemCount?: number;
  defaultItemHeight?: number;
  isScrolling?: (scrolling: boolean) => void;
}

const GroupedList = ({
  groups,
  className,
  renderItem,
  hideRecords,
  resetItems,
  scrollToIndex,
  toggleGroupIndex,
  initialItemCount,
  defaultItemHeight,
  isScrolling = () => {},
}: GroupedListProps): JSX.Element => {
  const classes = useStyles();
  const virtuoso = React.useRef<GroupedVirtuosoHandle>(null);

  const [groupCounts, setGroupCounts] = React.useState<number[]>(
    groups.map((e) => (e.expandOnLoad ? (e.data?.length ?? 0) : 0)),
  );
  const [openedGroups, setOpenedGroups] = React.useState<Set<number>>(
    new Set(groups.map((e, i) => (e.expandOnLoad && e.data ? i : -1)).filter((x) => x !== -1)),
  );
  const [groupedData, setGroupedData] = React.useState<unknown[]>(
    groups.reduce((prev, curr) => {
      if (curr.expandOnLoad && curr.data) {
        return [...prev, ...curr.data];
      }
      return prev;
    }, [] as unknown[]),
  );
  const [loadingGroup, setLoadingGroup] = React.useState<number | null>(null);

  const toggleGroup = (index: number, event?: React.MouseEvent) => {
    const { data: groupData, loadItems, headerOnToggle } = groups[index];
    if (headerOnToggle) headerOnToggle(event);

    const newGroupCount = [...groupCounts];

    let countIndex = 0;

    for (let i = 0; i < index; i += 1) {
      countIndex += newGroupCount[i];
    }

    if (openedGroups.has(index)) {
      newGroupCount[index] = 0;
      openedGroups.delete(index);
      groupedData.splice(countIndex, groupCounts[index]);
      setGroupedData(groupedData);
      setGroupCounts(newGroupCount);
    } else {
      openedGroups.add(index);

      if (groupData) {
        newGroupCount[index] = groupData.length;
        groupedData.splice(countIndex, 0, ...groupData);
        setGroupedData(groupedData);
        setGroupCounts(newGroupCount);
      } else {
        setLoadingGroup(index);

        loadItems()
          .then((result) => {
            newGroupCount[index] = result.length;
            setLoadingGroup(null);
            groupedData.splice(countIndex, 0, ...result);
            setGroupedData(groupedData);
            setGroupCounts(newGroupCount);
          })
          .catch(() => {
            setLoadingGroup(null);
          });
      }
    }
    setOpenedGroups(openedGroups);
  };

  React.useEffect(() => {
    if (resetItems) {
      let tempAccumNewGroupCount = [...groupCounts];
      openedGroups.forEach((index) => {
        const { data: groupData, loadItems } = groups[index];
        const newGroupCount = [...tempAccumNewGroupCount];
        let countIndex = 0;
        for (let i = 0; i < index; i += 1) {
          countIndex += newGroupCount[i];
        }
        if (groupData) {
          newGroupCount[index] = groupData.length;
          groupedData.splice(countIndex, groupCounts[index], ...groupData);
          setGroupedData(groupedData);
          setGroupCounts(newGroupCount);
          tempAccumNewGroupCount = newGroupCount;
        } else {
          setLoadingGroup(index);
          loadItems()
            .then((result) => {
              newGroupCount[index] = result.length;
              setLoadingGroup(null);
              groupedData.splice(countIndex, groupCounts[index], ...result);
              setGroupedData(groupedData);
              setGroupCounts(newGroupCount);
            })
            .catch(() => {
              setLoadingGroup(null);
            });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetItems]);

  React.useEffect(() => {
    if (scrollToIndex !== undefined && scrollToIndex >= 0 && scrollToIndex < groupedData.length) {
      virtuoso.current?.scrollToIndex({ index: scrollToIndex, align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  React.useEffect(() => {
    if (toggleGroupIndex !== undefined && toggleGroupIndex >= 0 && toggleGroupIndex < groups.length) {
      toggleGroup(toggleGroupIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleGroupIndex]);

  return (
    <GroupedVirtuoso
      className={clsx(classes.root, className)}
      ref={virtuoso}
      groupCounts={groupCounts}
      groupContent={(index) => {
        const { headerOnMouseEnter, headerOnMouseLeave, className: groupClassName } = groups[index];

        return (
          <div className={classes.groupHeaderContainer} data-testid={`group-${groups[index].title}`}>
            <div
              className={clsx(classes.groupHeader, groupClassName)}
              aria-hidden
              onMouseEnter={headerOnMouseEnter}
              onMouseLeave={headerOnMouseLeave}
              onClick={async (event: React.MouseEvent) => {
                if (loadingGroup) return;
                toggleGroup(index, event);
              }}
            >
              <Spring
                config={animationPaperConfig}
                to={{ transform: openedGroups.has(index) ? "rotate(180deg)" : "rotate(90deg)" }}
              >
                {(style) => <AnimatedExpandLessIcon className={classes.icon} style={style} />}
              </Spring>

              <div className={classes.groupTitle}>
                {groups[index].title}
                &nbsp;
                {!hideRecords && <>({groups[index].records})</>}
                <div className={classes.groupEndTitle}>{groups[index].endTitle}</div>
              </div>
            </div>
            {loadingGroup === index && <CardLoading />}
          </div>
        );
      }}
      itemContent={(index: number, groupIndex: number) => renderItem(index, groupedData, groupIndex)}
      initialItemCount={initialItemCount}
      defaultItemHeight={defaultItemHeight ?? 32}
      isScrolling={isScrolling}
    />
  );
};

export default GroupedList;
